import React from 'react'

import styled from 'styled-components'
import {palette} from 'styled-tools'
import classNames from 'classnames'
import {media} from 'styled-bootstrap-grid'

import {InfoTooltip} from '@festi/common/components/common'
import {Property} from '@festi/common/utils/products'
import {fluidRange} from '@festi/common/utils/styles'

interface Props {
  items: Property[]
  isCompact?: boolean
}

const Wrapper = styled.div`
  ${fluidRange('margin-bottom', '0px', '40px')};

  &.isCompact {
    width: 528px !important;
    max-width: 100%;
    margin-bottom: 0;
  }
`

const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  position: relative;

  &.isCompact {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  }

  &:nth-of-type(odd) {
    background-color: ${palette('backgroundGrey')};
  }

  ${media.md`
    justify-content: flex-start;
    flex-direction: row;
    align-items: center;
    gap: 52px;
    padding-inline: 20px;

    &.isCompact {
      justify-content: space-between;
      gap: 24px;
      padding-inline: 12px;
    }
  `}
`

const ItemText = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.1;
  text-align: left;
  color: ${palette('blue')};

  &:first-child {
    font-weight: 500;
    white-space: nowrap;
  }

  &:last-child {
    overflow: hidden;
  }
  &.isCompact:last-child {
    text-align: right;
  }

  ${media.md`
    &:first-child {
      width: 490px;
    }

    &:last-child {
      text-align: left;
      }

    &.isCompact:first-child {
      width: unset;
    }
  `}
`

const ListedItemText = styled.span`
  position: relative;
  padding-right: 4px;

  &:not(:last-child):after {
    content: ',';
  }
`

export default function ProductPropertiesTable({
  items,
  isCompact = false,
}: Props): JSX.Element {
  const cleanedList = items.flatMap(
    ({key, value, info, isProperty}: Property) => {
      if (value && isProperty) {
        return [{key, value, info, isProperty}]
      } else {
        return []
      }
    },
  )

  const itemsList =
    !!cleanedList.length && isCompact ? cleanedList.slice(0, 5) : cleanedList

  return (
    <Wrapper className={classNames({isCompact})}>
      {itemsList.map(({key, value, info}) => (
        <ListItem key={`${key}-${value}`} className={classNames({isCompact})}>
          <ItemText className={classNames({isCompact})}>
            {key}
            {!!info && (
              <InfoTooltip id={`product-property-${key}`}>{info}</InfoTooltip>
            )}
          </ItemText>

          <ItemText className={classNames({isCompact})}>
            {Array.isArray(value)
              ? value.map((val: string) => (
                  <ListedItemText key={val}>{val}</ListedItemText>
                ))
              : value || '--'}
          </ItemText>
        </ListItem>
      ))}
    </Wrapper>
  )
}
